/* @flow */
import * as React from 'react';
import { Link } from 'react-router-dom';
import { Redirect } from 'react-router';

import StatusIndicator from 'components/StatusIndicator/StatusIndicator';
import { customerUsers } from 'permissions/userTypes';
import * as statuses from 'utils/statuses';
import { weeksAndDays } from 'utils/durations';

import { formattedDate } from '../components/fieldComponents';
import createReportView from '../components/ReportView/ReportViewBase';
import type { ViewConfigGetter } from '../components/ReportView/ReportViewBase';
import type { DataViewColumn } from '../components/ReportView/types';
import { actionsListSingle, actionsListMultiple } from './actions';
import TableButton from '../../components/TableButton';
import { formulateUri } from '../components/ReportView/state';
import Moment from 'react-moment';

const Duration = ({ rowData, t }: *) =>
  statuses.getCode(rowData.er_order_type) !== 'sale' &&
  rowData.er_expected_end_date &&
  rowData.er_start_date ? (
    <span>{weeksAndDays(rowData.er_start_date, rowData.er_expected_end_date, t)}</span>
  ) : (
    <span>-</span>
  );

const columns: DataViewColumn[] = [
  {
    columnName: 'er_order_type',
    title: 'general:labels.type',
    flexBasis: 160,
    component: function ContractTypeCell({ display }) {
      return <StatusIndicator status={display} />;
    },
  },
  {
    columnName: 'er_contract_number',
    title: 'general:contracts.abbrs.number',
    flexBasis: 160,
    component: function ContractNo({ rowData, display }) {
      return <Link to={`/orders/${rowData.contract_id}`}>{display}</Link>;
    },
  },
  {
    columnName: 'er_po_number',
    title: 'general:contracts.abbrs.po_number',
    flexBasis: 160,
  },
  {
    columnName: 'er_start_date',
    title: 'general:labels.start_date',
    flexBasis: 150,
    component: formattedDate('DD-MM-YYYY'),
    toolTip: 'general:contracts.labels.first_shipment_date',
  },
  {
    columnName: 'er_expected_end_date',
    title: 'general:labels.expected_end_date',
    flexBasis: 150,
    component(props) {
      const date = props.display.split('T')[0];

      return statuses.getCode(props.rowData.er_order_type) !== 'sale' ? (
        <Moment date={date} format="MMMM DD, YYYY" />
      ) : (
        <span>-</span>
      );
    },
  },
  {
    columnName: 'duration',
    title: 'general:labels.duration',
    flexBasis: 180,
    component: Duration,
  },
  {
    columnName: 'er_status',
    title: 'general:contracts.labels.status',
    flexBasis: 100,
    component: function ContractStatusCell({ display }) {
      return <StatusIndicator status={display} />;
    },
  },
  {
    columnName: 'product_qty',
    title: '',
    flexBasis: 160,
    disableSort: true,
    component: function ContractProductQtyCell({ display, rowData }) {
      if (display === 0) {
        return <span />;
      }

      const pathname = `/orders/${rowData.id}#products`;
      const state = { productsExpanded: true };

      return (
        <Link
          to={{ pathname, state }}
          style={{ textDecoration: 'none' }}
          onClick={() => (window.location.href = pathname)}
        >
          <TableButton
            label={'general:actions.view_product_plural'}
            iconHref={'#icon-contracts'}
            onClick={() => {}}
          />
        </Link>
      );
    },
  },
  {
    columnName: '',
    title: '',
    flexBasis: 160,
    disableSort: true,
    component: function ContractsAssetsView({ rowData }) {
      const link = formulateUri('/assets', {
        page: 1,
        filters: [
          { key: 'contract_id', values: [rowData.contract_id] },
          { key: 'asset_status', values: ['On Rent', 'Sold'] },
        ],
      });
      return (
        <Link to={link} style={{ textDecoration: 'none' }}>
          <TableButton
            label={'general:actions.view_asset_plural'}
            iconHref={'#icon-assets'}
            onClick={() => {}}
          />
        </Link>
      );
    },
  },
];

const viewConfig: ViewConfigGetter = props => ({
  title: 'general:contract_plural',
  codename: 'contracts',
  type: 'SINGLE',
  userFilterNamespace: 'Contracts',
  defaultFilters: [
    ...(props.userStore.activeUser && customerUsers.includes(props.userStore.activeUser.role_id)
      ? []
      : [
          {
            key: 'er_status',
            values: ['open'],
          },
        ]),
  ],
  view: {
    columns,
    idKey: 'contract_id',
    dataPathname: 'as-contracts',
    apiType: 'AzureSearch',
    actions: {
      single: actionsListSingle,
      multi: actionsListMultiple,
    },
  },
});

export default createReportView(viewConfig);
